import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { FileText, Download, RefreshCw, Eye, ChevronLeft, Loader2, AlertCircle, CheckCircle, ZoomIn, ZoomOut, Code } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../../../layouts/SelfServePortalLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Portal-aligned colors (FundElite emerald dark / mint light) */
const getAnalysisUi = (resolvedTheme = 'light') => {
  const isDark = resolvedTheme === 'dark';
  return {
    isDark,
    pageBg: isDark ? '#081610' : '#F7F9F8',
    headerBg: isDark ? 'rgba(7,32,24,0.96)' : 'rgba(255,255,255,0.94)',
    headerBorder: isDark ? 'rgba(142,218,182,0.12)' : 'rgba(15,172,108,0.10)',
    surface: isDark ? 'rgba(10,34,25,0.78)' : 'rgba(255,255,255,0.88)',
    surfaceBorder: isDark ? 'rgba(142,218,182,0.12)' : 'rgba(15,172,108,0.10)',
    toolbarBg: isDark ? 'rgba(7,28,21,0.94)' : 'rgba(255,255,255,0.96)',
    previewBg: isDark ? '#0B1F18' : '#e2e8f0',
    chipBg: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
    text: {
      primary: isDark ? '#EAF7F0' : '#111827',
      secondary: isDark ? '#C7DDD0' : '#4B5563',
      muted: isDark ? '#93AE9F' : '#6B7280',
    },
    accent: isDark ? '#56B988' : '#0FAC6C',
    accentStrong: isDark ? '#8FE0B7' : '#0B8C58',
    accentBg: isDark ? 'rgba(15,172,108,0.16)' : 'rgba(16,185,129,0.10)',
    accentBorder: isDark ? 'rgba(15,172,108,0.22)' : 'rgba(16,185,129,0.20)',
    btnGhostBg: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
    primaryBtn: { bg: '#0FAC6C', hover: '#0D9F64', text: '#FFFFFF' },
    error: {
      bg: isDark ? 'rgba(239,68,68,0.12)' : '#fef2f2',
      border: isDark ? 'rgba(239,68,68,0.22)' : '#fecaca',
      title: isDark ? '#F27A7A' : '#991b1b',
      body: isDark ? '#FCA5A5' : '#b91c1c',
    },
    warn: {
      bg: isDark ? 'rgba(234,178,58,0.12)' : '#fffbeb',
      border: isDark ? 'rgba(234,178,58,0.22)' : '#fde68a',
      title: isDark ? '#F0C160' : '#92400e',
      body: isDark ? '#F0C160' : '#b45309',
    },
  };
};

/** US Letter at 96dpi — matches generated report HTML */
const PAGE_WIDTH_PX = 816;
const PAGE_HEIGHT_PX = 1056;

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

const ZOOM_LEVELS = [
  { label: 'Fit Width', value: 'fit-width' },
  { label: 'Fit Page', value: 'fit-page' },
  { label: '50%', value: 0.5 },
  { label: '75%', value: 0.75 },
  { label: '100%', value: 1 },
  { label: '125%', value: 1.25 },
];

const parseZoomValue = (raw) => {
  if (raw === 'fit' || raw === 'fit-width') return 'fit-width';
  if (raw === 'fit-page') return 'fit-page';
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : 'fit-width';
};

const ReportAnalysisPreview = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const ui = useMemo(() => getAnalysisUi(resolvedTheme), [resolvedTheme]);
  const [searchParams] = useSearchParams();
  const reportId = searchParams.get('report_id');

  const outlineBtnStyle = {
    borderColor: ui.surfaceBorder,
    color: ui.text.primary,
    background: ui.isDark ? 'rgba(255,255,255,0.06)' : 'transparent',
  };
  const outlineBtnClass = ui.isDark
    ? '!text-[#EAF7F0] !border-[rgba(142,218,182,0.22)] hover:!bg-[rgba(255,255,255,0.1)] hover:!text-[#EAF7F0]'
    : '!text-[#111827] hover:!bg-slate-50';
  
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [htmlContent, setHtmlContent] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [zoom, setZoom] = useState('fit-width');
  const [pageCount, setPageCount] = useState(0);
  const [previewScale, setPreviewScale] = useState(1);
  const [docHeight, setDocHeight] = useState(PAGE_HEIGHT_PX);

  const previewViewportRef = useRef(null);
  const previewScrollCanvasRef = useRef(null);
  const iframeRef = useRef(null);
  const [needsHorizontalScroll, setNeedsHorizontalScroll] = useState(false);

  // Check if analysis can be generated
  const checkStatus = useCallback(async () => {
    if (!reportId || !token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/report-analysis/${reportId}/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to check status');
      }
      
      const data = await response.json();
      setStatus(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [reportId, token]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  useEffect(() => {
    if (showPreview && typeof window !== 'undefined' && window.innerWidth < 768) {
      setZoom('fit-width');
    }
  }, [showPreview]);

  const computePreviewScale = useCallback((measuredHeight = docHeight) => {
    const container = previewScrollCanvasRef.current;
    if (!container) return 1;

    const pad = 12;
    const containerWidth = Math.max(container.clientWidth - pad, 200);
    const containerHeight = Math.max(container.clientHeight - pad, 200);
    const pageHeight = Math.max(measuredHeight || PAGE_HEIGHT_PX, PAGE_HEIGHT_PX);

    if (zoom === 'fit-width' || zoom === 'fit') {
      return clamp(containerWidth / PAGE_WIDTH_PX, 0.35, 1.25);
    }
    if (zoom === 'fit-page') {
      return clamp(
        Math.min(containerWidth / PAGE_WIDTH_PX, containerHeight / pageHeight),
        0.3,
        1
      );
    }
    if (typeof zoom === 'number') {
      return clamp(zoom, 0.35, 1.5);
    }
    return 1;
  }, [zoom, docHeight]);

  const updatePreviewScale = useCallback((heightOverride) => {
    const height = heightOverride ?? docHeight;
    const scale = computePreviewScale(height);
    setPreviewScale(scale);

    const container = previewScrollCanvasRef.current;
    if (container) {
      const pad = 12;
      const containerWidth = Math.max(container.clientWidth - pad, 200);
      const scaledWidth = PAGE_WIDTH_PX * scale;
      const isFitZoom = zoom === 'fit-width' || zoom === 'fit' || zoom === 'fit-page';
      setNeedsHorizontalScroll(!isFitZoom && scaledWidth > containerWidth + 1);
    }
  }, [computePreviewScale, docHeight, zoom]);

  const measureIframeDocument = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument;
    if (!doc?.body) return;

    const root = doc.getElementById('analysis-preview-root') || doc.body;
    const height = Math.max(
      root.scrollHeight,
      root.offsetHeight,
      doc.documentElement?.scrollHeight || 0,
      PAGE_HEIGHT_PX
    );
    setDocHeight(height);
    updatePreviewScale(height);
  }, [updatePreviewScale]);

  const handleIframeLoad = useCallback(() => {
    requestAnimationFrame(() => {
      measureIframeDocument();
      requestAnimationFrame(measureIframeDocument);
    });
  }, [measureIframeDocument]);

  useEffect(() => {
    if (!showPreview || !htmlContent) return;
    updatePreviewScale();
  }, [zoom, showPreview, htmlContent, docHeight, updatePreviewScale]);

  useEffect(() => {
    if (!showPreview) return undefined;

    const onResize = () => updatePreviewScale();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);

    const container = previewScrollCanvasRef.current;
    const ro = container ? new ResizeObserver(onResize) : null;
    if (container && ro) ro.observe(container);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      ro?.disconnect();
    };
  }, [showPreview, updatePreviewScale]);

  // Inject preview-specific CSS; parent applies transform scale to iframe
  const injectPreviewStyles = (html) => {
    const previewStyles = `
      <style id="fundelite-preview-styles">
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          overflow-x: hidden !important;
          background: #e2e8f0 !important;
          min-height: 100% !important;
        }
        body {
          padding: 16px 0 32px 0 !important;
          box-sizing: border-box !important;
        }
        #analysis-preview-root {
          width: ${PAGE_WIDTH_PX}px !important;
          max-width: ${PAGE_WIDTH_PX}px !important;
          margin: 0 auto !important;
          box-sizing: border-box !important;
          transform-origin: top center !important;
        }
        .pdf-page,
        .cover-page,
        .final-page {
          width: ${PAGE_WIDTH_PX}px !important;
          max-width: ${PAGE_WIDTH_PX}px !important;
          margin-left: auto !important;
          margin-right: auto !important;
          box-sizing: border-box !important;
          overflow: hidden !important;
        }
        .pdf-page {
          min-height: ${PAGE_HEIGHT_PX}px !important;
          height: auto !important;
          margin-bottom: 24px !important;
          background: #ffffff !important;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12) !important;
          border-radius: 6px !important;
        }
        .cover-page,
        .final-page {
          min-height: ${PAGE_HEIGHT_PX}px !important;
          height: auto !important;
          margin-bottom: 24px !important;
          padding: 0 !important;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12) !important;
          border-radius: 6px !important;
        }
        .pdf-page > *, .cover-page > *, .final-page > * {
          max-width: 100% !important;
          box-sizing: border-box !important;
        }
        .page-header { margin-bottom: 18px !important; }
        .data-table {
          table-layout: fixed !important;
          width: 100% !important;
        }
        @page { margin: 0; }
      </style>
    `;

    let modifiedHtml = html.replace('</head>', `${previewStyles}</head>`);
    if (!modifiedHtml.includes('id="analysis-preview-root"')) {
      modifiedHtml = modifiedHtml.replace(
        /<body([^>]*)>/i,
        '<body$1><div id="analysis-preview-root" class="analysis-preview-root">'
      );
      modifiedHtml = modifiedHtml.replace(/<\/body>/i, '</div></body>');
    }
    
    // Rewrite relative image paths to absolute URLs for browser preview
    // Convert paths like "bureaus/equifax.png" to full API URLs
    const assetsBaseUrl = `${API_URL}/api/report-assets`;
    modifiedHtml = modifiedHtml.replace(/src="bureaus\//g, `src="${assetsBaseUrl}/bureaus/`);
    modifiedHtml = modifiedHtml.replace(/src="illustrations\//g, `src="${assetsBaseUrl}/illustrations/`);
    modifiedHtml = modifiedHtml.replace(/src="providers\//g, `src="${assetsBaseUrl}/providers/`);
    modifiedHtml = modifiedHtml.replace(/src="provider-logos\//g, `src="${assetsBaseUrl}/provider-logos/`);
    
    return modifiedHtml;
  };

  // Generate HTML preview
  const generatePreview = async () => {
    if (!reportId || !token) return;
    
    setGenerating(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/report-analysis/${reportId}/pdf-html`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to generate preview');
      }
      
      const html = await response.text();
      // Inject preview-specific styles for proper page margins
      const styledHtml = injectPreviewStyles(html);
      setDocHeight(PAGE_HEIGHT_PX);
      setPreviewScale(1);
      setHtmlContent(styledHtml);
      setShowPreview(true);
      setZoom(typeof window !== 'undefined' && window.innerWidth < 768 ? 'fit-width' : zoom);
      
      // Count pages (rough estimate based on pdf-page divs)
      const pageMatches = html.match(/class="pdf-page"|class="cover-page"|class="final-page"/g);
      setPageCount(pageMatches ? pageMatches.length : 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  // Download PDF
  const downloadPdf = async () => {
    if (!reportId || !token) return;
    
    setDownloading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/report-analysis/${reportId}/download-pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to download PDF');
      }
      
      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `Credit_Report_Analysis_${reportId}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      // Download the PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading(false);
    }
  };

  // Download HTML (same as preview)
  const downloadHtml = async () => {
    if (!reportId || !token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/report-analysis/${reportId}/pdf-html`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to download HTML');
      }
      
      const html = await response.text();
      
      // Create a blob with the HTML content
      const blob = new Blob([html], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Credit_Report_Analysis_${reportId}.html`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err.message);
    }
  };

  // Zoom handlers
  const handleZoomIn = () => {
    const currentIndex = ZOOM_LEVELS.findIndex(z => z.value === zoom);
    if (currentIndex < ZOOM_LEVELS.length - 1) {
      setZoom(ZOOM_LEVELS[currentIndex + 1].value);
    }
  };

  const handleZoomOut = () => {
    const currentIndex = ZOOM_LEVELS.findIndex(z => z.value === zoom);
    if (currentIndex > 0) {
      setZoom(ZOOM_LEVELS[currentIndex - 1].value);
    }
  };

  if (loading) {
    return (
      <div
        className="flex items-center justify-center min-h-[50vh] rounded-2xl"
        style={{ background: ui.pageBg }}
      >
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: ui.accent }} />
          <p className="text-sm" style={{ color: ui.text.secondary }}>Loading report analysis...</p>
        </div>
      </div>
    );
  }

  if (!reportId) {
    return (
      <div className="p-4 sm:p-6 rounded-2xl" style={{ background: ui.pageBg }}>
        <div className="max-w-4xl mx-auto">
          <div
            className="rounded-xl p-6 text-center"
            style={{ background: ui.warn.bg, border: `1px solid ${ui.warn.border}` }}
          >
            <AlertCircle className="w-12 h-12 mx-auto mb-4" style={{ color: ui.warn.body }} />
            <h2 className="text-lg font-semibold mb-2" style={{ color: ui.warn.title }}>No Report Selected</h2>
            <p className="text-sm mb-4" style={{ color: ui.warn.body }}>
              Please select a credit report to generate an analysis.
            </p>
            <Button
              onClick={() => navigate('/credit-report')}
              variant="outline"
              style={outlineBtnStyle}
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Go to Credit Report
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col w-full min-w-0 max-w-full overflow-hidden rounded-xl sm:rounded-2xl -mx-2 sm:-mx-4 lg:-mx-6 h-[calc(100dvh-4.75rem)] sm:h-[calc(100dvh-5.5rem)] lg:h-[calc(100dvh-6rem)]"
      style={{ background: ui.pageBg }}
      data-testid="report-analysis-page"
    >
      {/* Header — stacks on mobile; portal-matched text colors */}
      <div
        className="shrink-0 px-3 sm:px-6 py-3 sm:py-4 border-b"
        style={{ background: ui.headerBg, borderColor: ui.headerBorder }}
      >
        <div className="max-w-7xl mx-auto space-y-3 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 min-w-0">
            <button
              type="button"
              onClick={() => navigate('/credit-report')}
              className="inline-flex items-center justify-center gap-1.5 self-start rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors min-h-[44px] min-w-[44px]"
              style={{
                color: ui.text.primary,
                background: ui.btnGhostBg,
                border: `1px solid ${ui.surfaceBorder}`,
              }}
              data-testid="back-to-credit-report-btn"
            >
              <ChevronLeft className="w-4 h-4 shrink-0" style={{ color: ui.accentStrong }} />
              <span style={{ color: ui.text.primary }}>Back</span>
            </button>
            <div className="min-w-0 flex-1">
              <h1
                className="text-base sm:text-xl font-semibold leading-snug truncate"
                style={{ color: ui.text.primary }}
              >
                Credit Report Analysis
              </h1>
              {status && (
                <p
                  className="text-xs sm:text-sm mt-0.5 truncate"
                  style={{ color: ui.text.muted }}
                  title={`${status.client_name} - Report ID: ${reportId}`}
                >
                  {status.client_name} — Report ID: {reportId.slice(0, 8)}…
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 w-full">
            <Button
              variant="outline"
              size="sm"
              className={`col-span-2 sm:col-span-1 sm:flex-1 sm:min-w-[7.5rem] sm:max-w-none min-h-[44px] ${outlineBtnClass}`}
              style={outlineBtnStyle}
              onClick={generatePreview}
              disabled={generating || !status?.available}
              data-testid="generate-preview-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin shrink-0" />
                  <span className="truncate">Generating…</span>
                </>
              ) : showPreview ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-1.5 shrink-0" />
                  <span className="truncate">Regenerate</span>
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-1.5 shrink-0" />
                  <span className="sm:hidden truncate">Preview</span>
                  <span className="hidden sm:inline truncate">Generate Preview</span>
                </>
              )}
            </Button>

            <Button
              size="sm"
              className="min-h-[44px] !text-white hover:!text-white"
              style={{ background: ui.primaryBtn.bg }}
              onClick={downloadPdf}
              disabled={downloading || !status?.available}
              data-testid="download-pdf-btn"
            >
              {downloading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin shrink-0" />
                  <span className="truncate">Downloading…</span>
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-1.5 shrink-0" />
                  <span className="sm:hidden truncate">PDF</span>
                  <span className="hidden sm:inline truncate">Download PDF</span>
                </>
              )}
            </Button>

            <Button
              size="sm"
              variant="outline"
              className={`min-h-[44px] ${outlineBtnClass}`}
              style={outlineBtnStyle}
              onClick={downloadHtml}
              disabled={!status?.available}
              data-testid="download-html-btn"
            >
              <Code className="w-4 h-4 mr-1.5 shrink-0" />
              <span className="sm:hidden truncate">HTML</span>
              <span className="hidden sm:inline truncate">Download HTML</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Content - flex-1 with min-h-0 to allow proper flex shrinking */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {error && (
          <div className="max-w-7xl mx-auto px-3 sm:px-6 pt-3 sm:pt-4">
            <div
              className="rounded-lg p-4 flex items-start gap-3"
              style={{ background: ui.error.bg, border: `1px solid ${ui.error.border}` }}
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: ui.error.title }} />
              <div className="min-w-0">
                <h3 className="font-medium" style={{ color: ui.error.title }}>Error</h3>
                <p className="text-sm break-words" style={{ color: ui.error.body }}>{error}</p>
              </div>
            </div>
          </div>
        )}

        {!showPreview ? (
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
            <div className="max-w-7xl mx-auto p-3 sm:p-6">
              <div
                className="rounded-xl border p-5 sm:p-8"
                style={{ background: ui.surface, borderColor: ui.surfaceBorder }}
              >
                <div className="max-w-2xl mx-auto text-center min-w-0">
                  <div
                    className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 sm:mb-6"
                    style={{ background: ui.accentBg, border: `1px solid ${ui.accentBorder}` }}
                  >
                    <FileText className="w-7 h-7 sm:w-8 sm:h-8" style={{ color: ui.accent }} />
                  </div>

                  <h2
                    className="text-xl sm:text-2xl font-bold mb-3"
                    style={{ color: ui.text.primary }}
                  >
                    Credit Report Analysis
                  </h2>

                  <p
                    className="text-sm sm:text-base mb-6 leading-relaxed px-1"
                    style={{ color: ui.text.secondary }}
                  >
                    Generate a comprehensive PDF analysis of your credit report.
                    This branded report includes score summaries, account details, utilization analysis,
                    and personalized action items.
                  </p>

                  {status?.available && (
                    <div
                      className="rounded-lg p-4 mb-6 text-left sm:text-center"
                      style={{ background: ui.accentBg, border: `1px solid ${ui.accentBorder}` }}
                    >
                      <div
                        className="flex items-center justify-center gap-2 font-medium text-sm sm:text-base"
                        style={{ color: ui.accentStrong }}
                      >
                        <CheckCircle className="w-5 h-5 shrink-0" />
                        <span>Report Ready for Analysis</span>
                      </div>
                      <p
                        className="text-xs sm:text-sm mt-2 break-words leading-relaxed"
                        style={{ color: ui.text.secondary }}
                      >
                        Client: {status.client_name} — Report Date:{' '}
                        {status.report_date ? new Date(status.report_date).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4">
                    <Button
                      size="lg"
                      onClick={generatePreview}
                      disabled={generating || !status?.available}
                      variant="outline"
                      className={`w-full sm:w-auto ${outlineBtnClass}`}
                      style={outlineBtnStyle}
                      data-testid="generate-preview-landing-btn"
                    >
                      {generating ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Generating Preview…
                        </>
                      ) : (
                        <>
                          <Eye className="w-5 h-5 mr-2" />
                          View Preview
                        </>
                      )}
                    </Button>

                    <Button
                      size="lg"
                      onClick={downloadPdf}
                      disabled={downloading || !status?.available}
                      className="w-full sm:w-auto text-white"
                      style={{ background: ui.primaryBtn.bg }}
                      data-testid="download-pdf-landing-btn"
                    >
                      {downloading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Downloading…
                        </>
                      ) : (
                        <>
                          <Download className="w-5 h-5 mr-2" />
                          Download PDF
                        </>
                      )}
                    </Button>
                  </div>

                  <div
                    className="mt-6 sm:mt-8 pt-5 sm:pt-6 border-t text-left"
                    style={{ borderColor: ui.surfaceBorder }}
                  >
                    <h3 className="text-sm font-medium mb-3" style={{ color: ui.text.secondary }}>
                      What&apos;s Included:
                    </h3>
                    <div
                      className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 text-sm"
                      style={{ color: ui.text.muted }}
                    >
                      {['Executive Summary', 'Bureau Scores', 'Utilization Analysis', 'Action Plan'].map((label) => (
                        <div key={label} className="flex items-center gap-2 min-w-0">
                          <CheckCircle className="w-4 h-4 shrink-0" style={{ color: ui.accent }} />
                          <span className="break-words">{label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="h-full flex flex-col min-h-0" data-testid="document-viewer-shell">
            <div
              className="border-b px-3 sm:px-4 py-2.5 sm:py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 shrink-0"
              style={{ background: ui.toolbarBg, borderColor: ui.headerBorder }}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Eye className="w-4 h-4 shrink-0" style={{ color: ui.accent }} />
                <span
                  className="text-sm font-semibold truncate"
                  style={{ color: ui.text.primary }}
                  data-testid="preview-title"
                >
                  Preview
                </span>
                {pageCount > 0 && (
                  <span
                    className="text-xs px-2 py-0.5 rounded shrink-0"
                    style={{ background: ui.chipBg, color: ui.text.muted }}
                  >
                    {pageCount} pages
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2 w-full min-w-0">
                {/* Mobile / tablet: compact zoom select */}
                <label className="flex items-center gap-2 md:hidden flex-1 min-w-0">
                  <span className="text-xs shrink-0" style={{ color: ui.text.muted }}>Zoom</span>
                  <select
                    value={String(zoom)}
                    onChange={(e) => {
                      setZoom(parseZoomValue(e.target.value));
                    }}
                    className="flex-1 min-w-0 h-9 rounded-lg px-2 text-xs font-medium border"
                    style={{
                      background: ui.chipBg,
                      borderColor: ui.surfaceBorder,
                      color: ui.text.primary,
                    }}
                    data-testid="zoom-select-mobile"
                  >
                    {ZOOM_LEVELS.map((level) => (
                      <option key={level.label} value={String(level.value)}>
                        {level.label}
                      </option>
                    ))}
                  </select>
                </label>

                <div
                  className="hidden md:flex items-center gap-1 rounded-lg p-1 shrink-0 max-w-full overflow-x-auto"
                  style={{ background: ui.chipBg }}
                  data-testid="zoom-controls"
                >
                  <button
                    type="button"
                    onClick={handleZoomOut}
                    className="h-8 w-8 flex items-center justify-center rounded-md shrink-0"
                    style={{ color: ui.text.primary }}
                    data-testid="zoom-out-btn"
                  >
                    <ZoomOut className="w-4 h-4" />
                  </button>
                  <div className="flex items-center gap-0.5">
                    {ZOOM_LEVELS.map((level) => (
                      <button
                        key={level.label}
                        type="button"
                        onClick={() => setZoom(level.value)}
                        className="h-8 px-2 text-xs shrink-0 rounded-md font-medium"
                        style={
                          zoom === level.value
                            ? { background: ui.surface, color: ui.text.primary }
                            : { color: ui.text.muted }
                        }
                        data-testid={`zoom-${level.label.toLowerCase().replace(' ', '-')}-btn`}
                      >
                        {level.label}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={handleZoomIn}
                    className="h-8 w-8 flex items-center justify-center rounded-md shrink-0"
                    style={{ color: ui.text.primary }}
                    data-testid="zoom-in-btn"
                  >
                    <ZoomIn className="w-4 h-4" />
                  </button>
                </div>

                <span className="text-xs hidden lg:inline shrink-0" style={{ color: ui.text.muted }}>
                  Scroll to view pages
                </span>
              </div>
            </div>

            <div
              ref={previewViewportRef}
              className="flex-1 min-h-0 w-full max-w-full overflow-hidden flex flex-col"
              style={{ background: ui.previewBg }}
              data-testid="preview-viewport"
            >
              <div
                ref={previewScrollCanvasRef}
                className="flex-1 min-h-0 w-full max-w-full overflow-auto overscroll-contain touch-pan-x touch-pan-y py-2 px-1 sm:px-2 box-border"
                style={{
                  WebkitOverflowScrolling: 'touch',
                  overscrollBehavior: 'contain',
                  touchAction: 'pan-x pan-y',
                  background: ui.previewBg,
                }}
                data-testid="preview-scroll-canvas"
              >
                {htmlContent && (
                  <div
                    className="relative shrink-0"
                    style={{
                      width: PAGE_WIDTH_PX * previewScale,
                      height: docHeight * previewScale,
                      minWidth: PAGE_WIDTH_PX * previewScale,
                      marginLeft: needsHorizontalScroll ? 0 : 'auto',
                      marginRight: needsHorizontalScroll ? 0 : 'auto',
                    }}
                    data-testid="preview-scale-wrapper"
                  >
                    <iframe
                      ref={iframeRef}
                      srcDoc={htmlContent}
                      title="Credit Report Analysis Preview"
                      onLoad={handleIframeLoad}
                      className="border-0 block absolute top-0 left-0"
                      style={{
                        width: PAGE_WIDTH_PX,
                        height: docHeight,
                        transform: `scale(${previewScale})`,
                        transformOrigin: 'top left',
                        border: 'none',
                        background: '#e2e8f0',
                        pointerEvents: 'auto',
                      }}
                      data-testid="preview-iframe"
                    />
                  </div>
                )}
              </div>
              {needsHorizontalScroll && (
                <p
                  className="text-center text-xs px-3 pb-2 shrink-0"
                  style={{ color: ui.text.muted }}
                  data-testid="preview-zoom-scroll-hint"
                >
                  Zoomed in. Scroll sideways to view the full page.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportAnalysisPreview;
